import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack} from '@mui/material';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {emitOnce} from '@ngneat/elf';
import {useParams} from 'react-router-dom';

import {SuppliersService, suppliersServicesQuery, suppliersServicesService} from '@store/suppliers/services';
import {searchSuppliersServicesEffect} from '@store/suppliers/services/suppliers-services.effects';
import {ManageClientService} from '@store/clients/services';

import ModalComponent from '@components/modal/Modal.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import SearchComponent from '@components/inputs/search/Search.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';

interface SelectServiceModalProps {
  handleClose: () => void;
  handleValidate: (supplierService: SuppliersService) => void;
  service: ManageClientService;
}

const SelectServiceModal = (props: SelectServiceModalProps) => {
  const {handleClose, handleValidate, service} = props;
  const {supplierId} = useParams();

  const {t} = useTranslation();

  const [showAddService, setShowAddService] = useState(false);

  const searchSuppliersServices = useEffectFn(searchSuppliersServicesEffect);

  const [{suppliersServices, loading}] = useObservable(suppliersServicesQuery.suppliersServices$);
  const [selectedSupplierService, setSelectedSupplierService] = useState<SuppliersService | undefined>(service.supplierService);

  const [filters] = useObservable(suppliersServicesQuery.filters$);
  const [sortModel] = useObservable(suppliersServicesQuery.sort$);
  const [tablePagination] = useObservable(suppliersServicesQuery.pagination$);

  useEffect(() => {
    if (supplierId) suppliersServicesService.resetFilters(supplierId);
  }, [supplierId]);

  useEffect(() => {
    searchSuppliersServices({filters, tablePagination, sort: sortModel});
  }, [filters, searchSuppliersServices, sortModel, tablePagination]);

  return (
    <>
      <ModalComponent
        aria-label="small-padding"
        handleClose={handleClose}
        maxWidth={false}
        title={t('clients.details.contractedServices.manage.selectService.title')}
        content={(
          <Stack pt="20px" height="85vh" width="100%" spacing={2} overflow="hidden">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Button variant="outlined" color="primary" onClick={() => setShowAddService(true)}>
                {t('clients.details.contractedServices.manage.selectService.addButton')}
              </Button>
              <TableFiltersComponent
                filters={filters}
                handleSave={suppliersServicesService.setFilters('tableFilters')}
                handleReset={() => supplierId ? suppliersServicesService.resetFilters(supplierId) : undefined}/>
            </Stack>
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  suppliersServicesService.setFilters('search')(value);
                  suppliersServicesService.setPagination('page')(1);
                });
              }}/>
            <NewTableComponent
              loading={loading}
              columns={[
                COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('suppliers.supplierServicesTable.id'), width: 100}),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'supplierOrgUnitService.supplierName',
                  label: t('clients.details.contractedServices.manage.selectService.table.name'),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.supplierName,
                  fullWidth: true,
                  minWidth: 350,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'organisationUnit.name',
                  label: t('clients.details.contractedServices.manage.selectService.table.supplier'),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.supplierUnitName,
                  fullWidth: true,
                  minWidth: 350,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'kind.kind',
                  label: t('clients.details.contractedServices.manage.selectService.table.serviceKind'),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.kindName,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  color: 'secondary',
                  field: 'availabilitiesCount',
                  label: t('clients.details.contractedServices.manage.selectService.table.availabilities'),
                  width: 130,
                }),
                COLUMN_TYPES.TEXT({
                  color: 'secondary',
                  field: 'container.name',
                  label: t('clients.details.contractedServices.manage.selectService.table.container'),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.containerName,
                  width: 200,
                }),
                COLUMN_TYPES.TEXT({
                  color: 'secondary',
                  field: 'waste.name',
                  label: t('clients.details.contractedServices.manage.selectService.table.wasteType'),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.wasteName,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  color: 'secondary',
                  field: 'buyPricesCount',
                  label: t('clients.details.contractedServices.manage.selectService.table.buyPrice'),
                  valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.buyPriceIfOnlyOne !== null
                    ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
                    : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
                  width: 130,
                }),
              ]}
              handlePageSizeChange={suppliersServicesService.setPagination('limit')}
              pageSize={tablePagination.limit}
              handlePageChange={(page) => suppliersServicesService.setPagination('page')(page + 1)}
              page={tablePagination.page - 1}
              handleSortModelChange={suppliersServicesService.setSort}
              sortModel={[sortModel]}
              singleRowSelection
              handleRowSelected={(selected) => {
                const supplierService = selected.length ? suppliersServices.find((s) => s.id === selected[0]) : undefined;
                setSelectedSupplierService(supplierService);
              }}
              rowsSelected={selectedSupplierService ? [selectedSupplierService.id] : []}
              totalRows={suppliersServicesQuery.totalRows}
              data={suppliersServices}/>
          </Stack>
        )}
        actions={(
          <>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t('global.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedSupplierService}
              onClick={() => selectedSupplierService ? handleValidate(selectedSupplierService) : undefined}>
              {t('global.validate')}
            </Button>
          </>
        )}/>
    </>
  );
};

export default SelectServiceModal;
